/**
 * Getting an authorization code for a MAILBOX.
 *
 * THE FLOW ITSELF IS `shared/oauth/popupAuthCode.ts`, shared with the calendar,
 * whose header carries the three defects that produced it. What is here is what
 * is about mail: which endpoint mints the URL, and which path each provider
 * redirects back to.
 *
 * SEPARATE PATHS FROM THE CALENDAR'S, not a shared one. Mail is a wider grant
 * than a calendar, and the redirect URI is registered per provider: sharing a
 * path would mean a change made for one grant silently applies to the other.
 * Both paths must be added to the provider registrations before this ships.
 */

import { beginMailboxAuthorization } from '@biasafe.queries/mailbox-provider-authorize/mailbox-provider-authorize';
import {
  isCallbackMessage,
  popupAuthCode,
  type AuthCode,
  type CallbackMessage,
} from '@/shared/oauth/popupAuthCode';

export type { AuthCode };
export type MailProvider = 'GOOGLE' | 'MICROSOFT';

export const MAILBOX_CALLBACK_SOURCE = 'biasafe-mailbox-callback';

export const MAILBOX_CALLBACK_PATHS: Record<MailProvider, string> = {
  GOOGLE: '/auth/google/mail-callback',
  MICROSOFT: '/auth/microsoft/mail-callback',
};

export type MailboxCallbackMessage = CallbackMessage;

export function isMailboxCallbackMessage(
  data: unknown,
): data is MailboxCallbackMessage {
  return isCallbackMessage(MAILBOX_CALLBACK_SOURCE, data);
}

export async function requestMailboxCode(
  provider: MailProvider,
): Promise<AuthCode> {
  return popupAuthCode({
    source: MAILBOX_CALLBACK_SOURCE,
    callbackPath: MAILBOX_CALLBACK_PATHS[provider],
    windowName: 'biasafe-mailbox-connect',
    startFailure: 'Could not start the mailbox connection.',
    begin: (redirectUri) =>
      beginMailboxAuthorization(provider.toLowerCase(), {
        redirect_uri: redirectUri,
      }),
  });
}
